import { useEffect, useMemo, useRef } from 'react';
import type { PoseTimerState } from '@modelcast/types';
import { logVideoDiagnostic, type VideoDiagnosticContext } from '../../video/videoDiagnostics';
import { useRoom } from '../RoomProvider';

const POSE_START_SOUND_PATH = '/sound/poseStart.mp3';
const POSE_END_COUNTDOWN_SOUND_PATH = '/sound/poseEnd.mp3';
const POSE_END_COUNTDOWN_START_SECONDS = 3;
const POSE_SOUND_LOG_PREFIX = '[PoseSound]';

interface UsePoseSoundEffectsParams {
  poseState: PoseTimerState;
  remaining: number;
}

export function usePoseSoundEffects({ poseState, remaining }: UsePoseSoundEffectsParams) {
  const { roomCode } = useRoom();
  const startSoundRef = useRef<HTMLAudioElement | null>(null);
  const endCountdownSoundRef = useRef<HTMLAudioElement | null>(null);
  const initializedRef = useRef(false);
  const lastPoseKeyRef = useRef<string | null>(null);
  const endCountdownPlayedForCueRef = useRef<string | null>(null);
  const previousRemainingRef = useRef<number | null>(null);
  const diagnosticContext = useMemo<VideoDiagnosticContext>(() => ({
    roomCode,
    participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    participantId: sessionStorage.getItem(`participant-id-${roomCode}`),
    participantName: sessionStorage.getItem(`participant-name-${roomCode}`),
  }), [roomCode]);

  useEffect(() => {
    startSoundRef.current = createPoseAudio(POSE_START_SOUND_PATH);
    endCountdownSoundRef.current = createPoseAudio(POSE_END_COUNTDOWN_SOUND_PATH);
    logPoseSound('audio-elements-created', {
      startSoundPath: POSE_START_SOUND_PATH,
      endSoundPath: POSE_END_COUNTDOWN_SOUND_PATH,
    });

    const unlockPoseAudio = () => {
      logPoseSound('unlock-attempted');
      void unlockPoseAudioElement(startSoundRef.current);
      void unlockPoseAudioElement(endCountdownSoundRef.current);
    };

    window.addEventListener('pointerdown', unlockPoseAudio, { once: true, passive: true });
    window.addEventListener('keydown', unlockPoseAudio, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlockPoseAudio);
      window.removeEventListener('keydown', unlockPoseAudio);
      stopPoseAudio(startSoundRef.current);
      stopPoseAudio(endCountdownSoundRef.current);
      startSoundRef.current = null;
      endCountdownSoundRef.current = null;
    };
  }, []);

  const activeTimedPose =
    poseState.isActive &&
    !poseState.isBreak &&
    !poseState.isUntimedBreak &&
    !poseState.isUntimed &&
    poseState.duration > 0;
  const canPlayPoseSound = activeTimedPose && !poseState.isPaused;
  const poseKey = activeTimedPose
    ? [
        poseState.startedAt ?? 'legacy',
        poseState.currentPose,
        poseState.poseName,
        poseState.duration,
      ].join(':')
    : null;
  const poseEndCueKey = activeTimedPose
    ? [
        poseState.currentPose,
        poseState.poseName,
        poseState.duration,
      ].join(':')
    : null;

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      lastPoseKeyRef.current = poseKey;
      previousRemainingRef.current = poseKey ? remaining : null;
      logPoseSound('initialized', {
        ...getPoseSoundLogState(poseState, {
          activeTimedPose,
          canPlayPoseSound,
          poseEndCueKey,
          poseKey,
          remaining,
          previousRemaining: previousRemainingRef.current,
        }),
      });
      if (poseEndCueKey && remaining <= POSE_END_COUNTDOWN_START_SECONDS) {
        endCountdownPlayedForCueRef.current = poseEndCueKey;
        logPoseSound('mounted-inside-end-window-marked-played', {
          ...getPoseSoundLogState(poseState, {
            activeTimedPose,
            canPlayPoseSound,
            poseEndCueKey,
            poseKey,
            remaining,
            previousRemaining: previousRemainingRef.current,
          }),
          threshold: POSE_END_COUNTDOWN_START_SECONDS,
        });
      }
      return;
    }

    if (!poseKey) {
      if (lastPoseKeyRef.current || previousRemainingRef.current !== null) {
        logPoseSound('pose-key-cleared', {
          ...getPoseSoundLogState(poseState, {
            activeTimedPose,
            canPlayPoseSound,
            poseEndCueKey,
            poseKey,
            remaining,
            previousRemaining: previousRemainingRef.current,
          }),
          previousPoseKey: lastPoseKeyRef.current,
          endCountdownPlayedForCue: endCountdownPlayedForCueRef.current,
        });
      }
      if (!poseState.isActive) {
        endCountdownPlayedForCueRef.current = null;
      }
      lastPoseKeyRef.current = null;
      previousRemainingRef.current = null;
      return;
    }

    if (lastPoseKeyRef.current !== poseKey) {
      const previousPoseKey = lastPoseKeyRef.current;
      lastPoseKeyRef.current = poseKey;
      previousRemainingRef.current = remaining;
      logPoseSound('pose-key-changed', {
        ...getPoseSoundLogState(poseState, {
          activeTimedPose,
          canPlayPoseSound,
          poseEndCueKey,
          poseKey,
          remaining,
          previousRemaining: previousRemainingRef.current,
        }),
        previousPoseKey,
        endCountdownPlayedForCue: endCountdownPlayedForCueRef.current,
      });
      if (canPlayPoseSound) {
        if (remaining <= POSE_END_COUNTDOWN_START_SECONDS) {
          if (poseEndCueKey && endCountdownPlayedForCueRef.current !== poseEndCueKey) {
            endCountdownPlayedForCueRef.current = poseEndCueKey;
            playPoseAudio(endCountdownSoundRef.current, 'pose-end', {
              ...getPoseSoundLogState(poseState, {
                activeTimedPose,
                canPlayPoseSound,
                poseEndCueKey,
                poseKey,
                remaining,
                previousRemaining: previousRemainingRef.current,
              }),
              reason: 'new-pose-key-already-in-end-window',
              threshold: POSE_END_COUNTDOWN_START_SECONDS,
              previousPoseKey,
            }, diagnosticContext);
          } else {
            logPoseSound('pose-end-play-skipped-already-played', {
              ...getPoseSoundLogState(poseState, {
                activeTimedPose,
                canPlayPoseSound,
                poseEndCueKey,
                poseKey,
                remaining,
                previousRemaining: previousRemainingRef.current,
              }),
              reason: 'new-pose-key-already-in-end-window',
              threshold: POSE_END_COUNTDOWN_START_SECONDS,
              previousPoseKey,
              endCountdownPlayedForCue: endCountdownPlayedForCueRef.current,
            });
          }
        } else {
          endCountdownPlayedForCueRef.current = null;
          playPoseAudio(startSoundRef.current, 'pose-start', {
            ...getPoseSoundLogState(poseState, {
              activeTimedPose,
              canPlayPoseSound,
              poseEndCueKey,
              poseKey,
              remaining,
              previousRemaining: previousRemainingRef.current,
            }),
            reason: 'new-pose-key',
          }, diagnosticContext);
        }
      } else {
        endCountdownPlayedForCueRef.current =
          poseEndCueKey && remaining <= POSE_END_COUNTDOWN_START_SECONDS
            ? poseEndCueKey
            : null;
        logPoseSound('pose-key-changed-sound-not-playable', {
          ...getPoseSoundLogState(poseState, {
            activeTimedPose,
            canPlayPoseSound,
            poseEndCueKey,
            poseKey,
            remaining,
            previousRemaining: previousRemainingRef.current,
          }),
          threshold: POSE_END_COUNTDOWN_START_SECONDS,
          endCountdownPlayedForCue: endCountdownPlayedForCueRef.current,
        });
      }
      return;
    }

    if (!canPlayPoseSound) {
      logPoseSound('sound-not-playable', {
        ...getPoseSoundLogState(poseState, {
        activeTimedPose,
        canPlayPoseSound,
        poseEndCueKey,
        poseKey,
        remaining,
        previousRemaining: previousRemainingRef.current,
      }),
      endCountdownPlayedForCue: endCountdownPlayedForCueRef.current,
    });
      return;
    }

    const previousRemaining = previousRemainingRef.current;
    previousRemainingRef.current = remaining;

    // poseEnd.mp3 includes three lead-in ticks; its fourth tick should land exactly at zero.
    if (
      poseEndCueKey &&
      endCountdownPlayedForCueRef.current !== poseEndCueKey &&
      previousRemaining !== null &&
      previousRemaining > POSE_END_COUNTDOWN_START_SECONDS &&
      remaining <= POSE_END_COUNTDOWN_START_SECONDS
    ) {
      endCountdownPlayedForCueRef.current = poseEndCueKey;
      playPoseAudio(endCountdownSoundRef.current, 'pose-end', {
        ...getPoseSoundLogState(poseState, {
          activeTimedPose,
          canPlayPoseSound,
          poseEndCueKey,
          poseKey,
          remaining,
          previousRemaining,
        }),
        reason: 'remaining-crossed-end-threshold',
        threshold: POSE_END_COUNTDOWN_START_SECONDS,
      }, diagnosticContext);
    }
  }, [canPlayPoseSound, diagnosticContext, poseEndCueKey, poseKey, remaining]);
}

function createPoseAudio(src: string) {
  if (typeof Audio === 'undefined') return null;

  const audio = new Audio(src);
  audio.preload = 'auto';
  audio.load();
  return audio;
}

function playPoseAudio(
  audio: HTMLAudioElement | null,
  cue: 'pose-start' | 'pose-end',
  details: Record<string, unknown> = {},
  diagnosticContext?: VideoDiagnosticContext
) {
  if (!audio) {
    logPoseSound('play-skipped-no-audio-element', { cue, ...details });
    logVideoDiagnostic('warn', 'pose-sound-play-skipped-no-audio-element', { cue, ...details }, diagnosticContext);
    return;
  }

  const previousCurrentTime = audio.currentTime;
  const previousPaused = audio.paused;
  logPoseSound('play-requested', {
    cue,
    ...details,
    audioDuration: audio.duration,
    audioReadyState: audio.readyState,
    previousCurrentTime,
    previousPaused,
  });
  logVideoDiagnostic('info', 'pose-sound-play-requested', {
    cue,
    ...details,
    audioDuration: audio.duration,
    audioReadyState: audio.readyState,
    previousCurrentTime,
    previousPaused,
  }, diagnosticContext);

  audio.pause();
  audio.currentTime = 0;
  void audio.play().then(() => {
    logPoseSound('play-started', {
      cue,
      ...details,
      audioDuration: audio.duration,
      audioReadyState: audio.readyState,
    });
    logVideoDiagnostic('info', 'pose-sound-play-started', {
      cue,
      ...details,
      audioDuration: audio.duration,
      audioReadyState: audio.readyState,
    }, diagnosticContext);
  }).catch((error: unknown) => {
    logPoseSound('play-failed', {
      cue,
      ...details,
      errorName: error instanceof DOMException ? error.name : undefined,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    logVideoDiagnostic('warn', 'pose-sound-play-failed', {
      cue,
      ...details,
      errorName: error instanceof DOMException ? error.name : undefined,
      errorMessage: error instanceof Error ? error.message : String(error),
    }, diagnosticContext);
    // Browsers may block audio until the user has interacted with the page.
  });
}

async function unlockPoseAudioElement(audio: HTMLAudioElement | null) {
  if (!audio) return;

  const previousVolume = audio.volume;
  audio.volume = 0;
  audio.currentTime = 0;

  try {
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    logPoseSound('unlock-succeeded', {
      src: audio.currentSrc || audio.src,
      readyState: audio.readyState,
    });
  } catch (error) {
    logPoseSound('unlock-failed', {
      src: audio.currentSrc || audio.src,
      errorName: error instanceof DOMException ? error.name : undefined,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    // The regular play path will try again when a pose cue is due.
  } finally {
    audio.volume = previousVolume;
  }
}

function stopPoseAudio(audio: HTMLAudioElement | null) {
  if (!audio) return;
  audio.pause();
  audio.currentTime = 0;
}

function getPoseSoundLogState(
  poseState: PoseTimerState,
  derived: {
    activeTimedPose: boolean;
    canPlayPoseSound: boolean;
    poseEndCueKey: string | null;
    poseKey: string | null;
    previousRemaining: number | null;
    remaining: number;
  }
) {
  return {
    activeTimedPose: derived.activeTimedPose,
    canPlayPoseSound: derived.canPlayPoseSound,
    currentPose: poseState.currentPose,
    duration: poseState.duration,
    isActive: poseState.isActive,
    isBreak: poseState.isBreak,
    isPaused: poseState.isPaused,
    isUntimed: poseState.isUntimed,
    isUntimedBreak: poseState.isUntimedBreak,
    poseEndCueKey: derived.poseEndCueKey,
    poseKey: derived.poseKey,
    poseName: poseState.poseName,
    previousRemaining: derived.previousRemaining,
    remaining: derived.remaining,
    startedAt: poseState.startedAt ?? null,
  };
}

function logPoseSound(event: string, details: Record<string, unknown> = {}) {
  console.info(POSE_SOUND_LOG_PREFIX, event, {
    loggedAt: new Date().toISOString(),
    ...details,
  });
}
